import { useEffect, useState, useCallback } from "react";
import { fetchShipmentDataById } from "../../../../../services/ShipperServices/Shipments/shipments.services";
import { showSnackBar } from "../../../../../store/common/snackBarSlice";
import { useDispatch } from "react-redux";
import ModalWrapper from "../../../../../Components/Modals/ModalWrapper";
import ShipmentStatusModalSkeleton from "./Skeleton";
import { updatePackageStatus } from "../../../../../services/CarrierServices/shipments.services";
import { MdQrCodeScanner } from "react-icons/md";
import { trackingStatuses } from "../../../../../constants/statuses";
import { AiOutlineClose } from "react-icons/ai";
import BarcodeScanner from "./BarcodeScanner";
import Button from "../../../../../Components/UI/Buttons/Button";
import BoltButton from "../../../../../Components/UI/Buttons/BoltButton";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaChevronRight,
  FaChevronDown,
  FaImage,
  FaStickyNote,
  FaCheck,
  FaPen,
} from "react-icons/fa";
import { BiSolidCheckCircle } from "react-icons/bi";
import PackageDetails from "./PackageDetails";
import TrackingHistory from "./TrackingHistory";
import ShipmentDetails from "./ShipmentDetails";
import { useTranslation } from "react-i18next";
import i18n from "../../../../../i18n";

const ShipmentStatusModal = ({
  shipmentId,
  onClose,
  isOpen,
  setRefresh,
  drivers,
}) => {
  const { t } = useTranslation("carrier");
  const [shipmentData, setShipmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [updatedPackages, setUpdatedPackages] = useState([]);
  const [formData, setFormData] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [scannedPackages, setScannedPackages] = useState();
  const [barcodeInput, setBarcodeInput] = useState("");
  const [scanAlert, setScanAlert] = useState({
    show: false,
    type: "success",
    text: "",
    barcode: "",
  });

  // Bulk selection and actions state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkNote, setBulkNote] = useState("");
  const [bulkImages, setBulkImages] = useState([]);
  const [bulkImagePreview, setBulkImagePreview] = useState(null);
  const [bulkActiveActions, setBulkActiveActions] = useState(["status"]); // Multiple tabs can be open, status open by default
  const [bulkSignature, setBulkSignature] = useState(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedImageModal, setSelectedImageModal] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0,
  });
  const [scannerVisible, setScannerVisible] = useState(false);
  const [historyCollapsed, setHistoryCollapsed] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);

  const dispatch = useDispatch();

  const packageExists = shipmentData?.shipment_details?.some(
    (pkg) => pkg.barcode === barcodeInput
  );

  const isPickedUpOrLater = (pkg) => {
    const currentStatus =
      formData.find((p) => p.id === pkg.barcode)?.status || "";
    return (
      trackingStatuses.indexOf(currentStatus) >=
      trackingStatuses.indexOf("Picked Up")
    );
  };

  // Check if signature should be shown for selected packages
  const shouldShowSignature = () => {
    return selectedIds.some((id) => {
      const currentFormData = formData.find((p) => p.id === id);
      const pkg = shipmentData?.shipment_details?.find((p) => p.barcode === id);
      const currentStatus =
        currentFormData?.status || pkg?.tracking_status || "";
      return currentStatus === "Delivered" && shipmentData?.signature === true;
    });
  };

  // Get package images for display (historical + newly uploaded)
  const getPackageImages = (pkg) => {
    const formDataEntry = formData.find((p) => p.id === pkg.barcode);
    const newlyUploadedImages = formDataEntry?.images || [];

    // Get all historical images from shipment_histories
    const historicalImages = [];
    if (pkg.shipment_histories && Array.isArray(pkg.shipment_histories)) {
      pkg.shipment_histories.forEach((entry) => {
        if (entry.status_media && Array.isArray(entry.status_media)) {
          entry.status_media.forEach((media) => {
            if (media.file) {
              historicalImages.push({
                src: media.file,
                type: "historical",
              });
            }
          });
        }
      });
    }

    // Convert newly uploaded files to objects with consistent format
    const newImages = newlyUploadedImages.map((file) => ({
      src: URL.createObjectURL(file),
      type: "new",
      file: file,
    }));

    // Combine historical and new images
    return [...historicalImages, ...newImages];
  };

  const getShipmentDataById = useCallback(
    async (id) => {
      setLoading(true);
      let response = await fetchShipmentDataById(id);
      if (!response.error) {
        setLoading(false);
        setShipmentData(response?.data?.result);
        setFormData(
          response?.data?.result?.shipment_details?.map((pkg) => {
            return {
              id: pkg?.barcode,
              status: pkg?.tracking_status,
              description: "",
            };
          })
        );
      } else {
        setLoading(false);
        dispatch(
          showSnackBar({
            text:
              response?.error?.response?.data?.message ??
              i18n.t("common:messages.somethingWentWrong"),
            severity: "error",
          })
        );
      }
    },
    [dispatch]
  );
  const updateFormData = (pkgId, key, value) => {
    setFormData((prevData) => {
      const existingIndex = prevData.findIndex((item) => item.id === pkgId);
      if (existingIndex !== -1) {
        const updatedData = [...prevData];
        updatedData[existingIndex] = {
          ...updatedData[existingIndex],
          [key]: value,
        };
        return updatedData;
      } else {
        return [
          ...prevData,
          { id: pkgId, images: [], description: "", status: "" },
        ];
      }
    });
  };

  const handleImageUpload = (pkg, e) => {
    const files = Array.from(e.target.files);

    setFormData((prevData) => {
      const existingIndex = prevData.findIndex(
        (item) => item.id === pkg.barcode
      );
      if (existingIndex !== -1) {
        const updatedData = [...prevData];
        updatedData[existingIndex].images = [
          ...(updatedData[existingIndex].images || []),
          ...files,
        ];
        return updatedData;
      } else {
        return [
          ...prevData,
          { id: pkg.id, images: files, description: "", status: "" },
        ];
      }
    });
  };
  const handlePackageClick = (pkg) => {
    setSelectedPackage(pkg);
  };

  const handleBackToList = () => {
    setSelectedPackage(null);
  };

  const handleScanSuccess = (scannedBarcode) => {
    if (
      shipmentData?.shipment_details?.some(
        (pkg) => pkg.barcode === scannedBarcode
      )
    ) {
      // Find the package to check its current status
      const scannedPackage = shipmentData.shipment_details.find(
        (pkg) => pkg.barcode === scannedBarcode
      );

      // Get current status from formData or fallback to package status
      const currentFormData = formData.find((p) => p.id === scannedBarcode);
      const currentStatus =
        currentFormData?.status || scannedPackage?.tracking_status || "";

      // If status is less than "Picked Up", update to "Picked Up"
      if (
        trackingStatuses.indexOf(currentStatus) <
        trackingStatuses.indexOf("Picked Up")
      ) {
        updateFormData(scannedBarcode, "status", "Picked Up");
      }

      // Always select the scanned package
      setSelectedIds((prev) =>
        prev.includes(scannedBarcode) ? prev : [...prev, scannedBarcode]
      );

      setScanAlert({
        show: true,
        type: "success",
        text: t("dispatcher.scanned"),
        barcode: scannedBarcode,
      });
    } else {
      setScanAlert({
        show: true,
        type: "error",
        text: t("common:empty.notFound"),
        barcode: scannedBarcode,
      });
    }
    // Auto-hide alert
    setTimeout(
      () =>
        setScanAlert({ show: false, type: "success", text: "", barcode: "" }),
      1800
    );
  };

  const handlePickupByBarcode = (barcode) => {
    setBarcodeInput("");

    // Find the package to check its current status
    const scannedPackage = shipmentData.shipment_details.find(
      (pkg) => pkg.barcode === barcode
    );

    // Get current status from formData or fallback to package status
    const currentFormData = formData.find((p) => p.id === barcode);
    const currentStatus =
      currentFormData?.status || scannedPackage?.tracking_status || "";

    // If status is less than "Picked Up", update to "Picked Up"
    if (
      trackingStatuses.indexOf(currentStatus) <
      trackingStatuses.indexOf("Picked Up")
    ) {
      updateFormData(barcode, "status", "Picked Up");
    }

    // Always select the package
    setSelectedIds((prev) =>
      prev.includes(barcode) ? prev : [...prev, barcode]
    );
  };

  // Selection helpers - all packages are now eligible for actions
  const isEligibleForActions = (pkg) => {
    return true; // Allow all packages to be selected and updated
  };

  const toggleSelect = (id, eligible) => {
    if (!eligible) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const clearSelection = () => setSelectedIds([]);

  const getEligibleIds = () =>
    shipmentData?.shipment_details?.map((pkg) => pkg.barcode) || [];

  const toggleSelectAll = () => {
    const eligibleIds = getEligibleIds();
    const allSelected =
      eligibleIds.length > 0 &&
      eligibleIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : eligibleIds);
  };

  // Bulk image handlers

  const handleBulkRetake = () => {
    setBulkImages([]);
    if (bulkImagePreview) URL.revokeObjectURL(bulkImagePreview);
    setBulkImagePreview(null);
  };

  // Toggle bulk actions
  const toggleBulkAction = (action) => {
    // Prevent status tab from being unselected
    if (action === "status") return;

    setBulkActiveActions((prev) =>
      prev.includes(action)
        ? prev.filter((a) => a !== action)
        : [...prev, action]
    );
  };

  // Auto-apply bulk actions when values change
  const handleBulkStatusChange = (status) => {
    setBulkStatus(status);
    if (status && selectedIds.length > 0) {
      selectedIds.forEach((id) => updateFormData(id, "status", status));
    }
    setValidationErrors({});
  };

  const handleBulkNoteChange = (note) => {
    setBulkNote(note);
    if (note && selectedIds.length > 0) {
      selectedIds.forEach((id) => updateFormData(id, "description", note));
    }
    setValidationErrors({});
  };

  const handleBulkImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    processBulkImages(files);
  };

  const processBulkImages = useCallback(
    (files) => {
      // Filter to ensure only valid image files
      const validImageFiles = files.filter((file) => {
        const allowedImageTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif",
          "image/webp",
          "image/bmp",
          "image/tiff",
        ];
        return (
          allowedImageTypes.includes(file.type.toLowerCase()) &&
          file.size <= 10 * 1024 * 1024
        ); // 10MB limit
      });

      setBulkImages(validImageFiles);
      if (validImageFiles[0]) {
        const url = URL.createObjectURL(validImageFiles[0]);
        setBulkImagePreview(url);
      } else {
        setBulkImagePreview(null);
      }

      // Auto-apply images to selected packages
      if (validImageFiles.length > 0 && selectedIds.length > 0) {
        setFormData((prevData) => {
          const updated = [...prevData];
          selectedIds.forEach((id) => {
            const idx = updated.findIndex((x) => x.id === id);
            if (idx !== -1) {
              updated[idx].images = [
                ...(updated[idx].images || []),
                ...validImageFiles,
              ];
            } else {
              updated.push({
                id,
                images: [...validImageFiles],
                description: "",
                status: "",
              });
            }
          });
          return updated;
        });
      }
    },
    [selectedIds]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const allowedImageTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/bmp",
      "image/tiff",
    ];

    const files = Array.from(e.dataTransfer.files).filter((file) =>
      allowedImageTypes.includes(file.type.toLowerCase())
    );

    if (files.length > 0) {
      processBulkImages(files);
    }
  };

  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const allowedImageTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/bmp",
        "image/tiff",
      ];

      const imageFiles = [];
      for (let i = 0; i < items.length; i++) {
        if (allowedImageTypes.includes(items[i].type.toLowerCase())) {
          const file = items[i].getAsFile();
          if (file) {
            imageFiles.push(file);
          }
        }
      }

      if (imageFiles.length > 0) {
        processBulkImages(imageFiles);
      }
    },
    [processBulkImages]
  );

  const handleBulkSignatureChange = (signature) => {
    setBulkSignature(signature);

    // Auto-apply signature to selected packages
    if (signature && selectedIds.length > 0) {
      setFormData((prevData) => {
        const updated = [...prevData];
        selectedIds.forEach((id) => {
          const idx = updated.findIndex((x) => x.id === id);
          if (idx !== -1) {
            updated[idx].images = [...(updated[idx].images || []), signature];
          } else {
            updated.push({
              id,
              images: [signature],
              description: "",
              status: "",
            });
          }
        });
        return updated;
      });
    }
  };

  const handleUpdatePackages = async () => {
    if (!formData.length) return;

    setUpdating(true);

    const data = new FormData();

    formData.forEach((pkg, index) => {
      if (
        ![null, "null", undefined].includes(pkg.status) &&
        (!shipmentData.shipment_details?.find(
          (pakg) => pakg.tracking_status === pkg.status
        ) ||
          pkg.description)
      ) {
        data.append(`packages[${index}][id]`, pkg.id);
        data.append(`packages[${index}][status]`, pkg.status);
        data.append(`packages[${index}][description]`, pkg.description || "");

        if (pkg.images) {
          pkg.images.forEach((image) => {
            data.append(`packages[${index}][images][]`, image);
          });
        }
      }
    });

    let response = await updatePackageStatus(data);

    if (!response.error) {
      setUpdating(false);
      dispatch(
        showSnackBar({
          text: t("dispatcher.shipmentStatusUpdatedSuccessfully"),
          severity: "success",
        })
      );
      setRefresh(Math.floor(Math.random() * 100) + 1);
      handleModalClose();
    } else {
      setUpdating(false);
      dispatch(
        showSnackBar({
          text:
            response?.error?.response?.data?.message ?? t("common:messages.somethingWentWrong"),
          severity: "error",
        })
      );
    }
  };

  const handleModalClose = () => {
    // Clear all bulk selection states when modal closes
    setSelectedIds([]);
    setBulkStatus("");
    setBulkNote("");
    setBulkImages([]);
    setBulkImagePreview(null);
    setBulkActiveActions(["status"]); // Reset to default open tab
    setBulkSignature(null);
    setSignatureModalOpen(false);
    setValidationErrors({});
    setSelectedImageModal({ isOpen: false, images: [], currentIndex: 0 });
    setScannerVisible(false);
    setHistoryCollapsed(true); // Reset history to collapsed state
    setIsDragOver(false); // Reset drag state
    onClose();
  };

  useEffect(() => {
    if (shipmentId) {
      getShipmentDataById(shipmentId);
    }
  }, [shipmentId, getShipmentDataById]);

  // Add paste event listener for images
  useEffect(() => {
    if (isOpen && selectedIds.length > 0) {
      document.addEventListener("paste", handlePaste);
      return () => {
        document.removeEventListener("paste", handlePaste);
      };
    }
  }, [isOpen, selectedIds.length, handlePaste]);

  useEffect(() => {
    const newScannedPackages = new Set();
    const updatedPackageIds = new Set();

    shipmentData?.shipment_details?.forEach((pkg) => {
      if (
        trackingStatuses.includes(pkg.tracking_status) &&
        trackingStatuses.indexOf(pkg.tracking_status) >=
          trackingStatuses.indexOf("Picked Up")
      ) {
        newScannedPackages.add(pkg.barcode);
      }
    });

    formData?.forEach((pkg) => {
      if (
        trackingStatuses.includes(pkg.status) &&
        trackingStatuses.indexOf(pkg.status) >=
          trackingStatuses.indexOf("Picked Up")
      ) {
        newScannedPackages.add(pkg.id);
      }
    });

    formData?.forEach((formPkg) => {
      const matchingPkg = shipmentData?.shipment_details?.find(
        (pkg) => pkg.barcode === formPkg.id
      );
      if (
        matchingPkg &&
        (matchingPkg.tracking_status !== formPkg.status ||
          (formPkg.description && formPkg.description !== ""))
      ) {
        updatedPackageIds.add(formPkg.id);
      }
    });

    setScannedPackages(Array.from(newScannedPackages));
    setUpdatedPackages(Array.from(updatedPackageIds));
  }, [shipmentData, formData]);

  // Initialize signature canvas when modal opens
  useEffect(() => {
    if (signatureModalOpen) {
      const canvas = document.getElementById("signature-canvas");
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      let isDrawing = false;
      let lastX = 0;
      let lastY = 0;

      // Set up canvas
      ctx.strokeStyle = "var(--color-ui-text)";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      const startDrawing = (e) => {
        isDrawing = true;
        const rect = canvas.getBoundingClientRect();
        lastX = e.clientX - rect.left;
        lastY = e.clientY - rect.top;
      };

      const draw = (e) => {
        if (!isDrawing) return;
        const rect = canvas.getBoundingClientRect();
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;

        ctx.beginPath();
        ctx.moveTo(lastX, lastY);
        ctx.lineTo(currentX, currentY);
        ctx.stroke();

        lastX = currentX;
        lastY = currentY;
      };

      const stopDrawing = () => {
        isDrawing = false;
      };

      // Mouse events
      canvas.addEventListener("mousedown", startDrawing);
      canvas.addEventListener("mousemove", draw);
      canvas.addEventListener("mouseup", stopDrawing);
      canvas.addEventListener("mouseout", stopDrawing);

      // Touch events for mobile
      const getTouchPos = (e) => {
        const rect = canvas.getBoundingClientRect();
        return {
          x: e.touches[0].clientX - rect.left,
          y: e.touches[0].clientY - rect.top,
        };
      };

      const touchStart = (e) => {
        e.preventDefault();
        const touch = getTouchPos(e);
        lastX = touch.x;
        lastY = touch.y;
        isDrawing = true;
      };

      const touchMove = (e) => {
        e.preventDefault();
        if (!isDrawing) return;
        const touch = getTouchPos(e);

        ctx.beginPath();
        ctx.moveTo(lastX, lastY);
        ctx.lineTo(touch.x, touch.y);
        ctx.stroke();

        lastX = touch.x;
        lastY = touch.y;
      };

      const touchEnd = (e) => {
        e.preventDefault();
        isDrawing = false;
      };

      canvas.addEventListener("touchstart", touchStart);
      canvas.addEventListener("touchmove", touchMove);
      canvas.addEventListener("touchend", touchEnd);

      // Cleanup
      return () => {
        canvas.removeEventListener("mousedown", startDrawing);
        canvas.removeEventListener("mousemove", draw);
        canvas.removeEventListener("mouseup", stopDrawing);
        canvas.removeEventListener("mouseout", stopDrawing);
        canvas.removeEventListener("touchstart", touchStart);
        canvas.removeEventListener("touchmove", touchMove);
        canvas.removeEventListener("touchend", touchEnd);
      };
    }
  }, [signatureModalOpen]);

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={() => {}}
      boxClassName="relative py-3 max-h-[90vh] overflow-y-auto"
    >
      {loading ? (
        <ShipmentStatusModalSkeleton />
      ) : (
        <AnimatePresence mode="wait">
          {selectedPackage ? (
            <motion.div
              key="details"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{
                duration: 0.3,
                ease: [0.4, 0.0, 0.2, 1], // cubic-bezier for smooth motion
              }}
            >
              <PackageDetails
                key={selectedPackage.id}
                pkg={selectedPackage}
                shipmentData={shipmentData}
                onAddImage={handleImageUpload}
                updateFormData={updateFormData}
                formData={formData}
                goBack={handleBackToList}
              />
            </motion.div>
          ) : (
            <motion.div
              key="main"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{
                duration: 0.3,
                ease: [0.4, 0.0, 0.2, 1], // cubic-bezier for smooth motion
              }}
              className="relative"
            >
              <AiOutlineClose
                size={16}
                className="absolute right-0 top-0 cursor-pointer"
                onClick={handleModalClose}
              />
              <div className="mb-3 space-y-1">
                <h2 className="text-lg font-jakarta font-semibold text-ui-text">
                  {t("dispatcher.updateShipmentStatus")}
                </h2>
                <div className="text-[10px] text-ui-text-muted">
                  {t("dispatcher.shipmentIdLabel")} {shipmentData?.shipment_tracking_id ?? "-"}{" "}
                </div>
                <div className="text-[10px] text-ui-text-muted">
                  {t("dispatcher.boltIdLabel")} {shipmentData?.tracking_id ?? "-"}{" "}
                </div>
              </div>
              <ShipmentDetails
                shipmentData={shipmentData}
                scannedPackages={scannedPackages}
                drivers={drivers}
              />
              {/* One-line Scanner */}
              <div className="w-full mt-2">
                <h3 className="text-xs font-semibold mb-1 text-ui-text">
                  {t("dispatcher.packageScanner")}
                </h3>
                <div className="flex gap-2">
                  {/* Scan button */}
                  <BoltButton
                    icon={MdQrCodeScanner}
                    label={t("dispatcher.scan")}
                    onClick={() => {
                      setBarcodeInput("");
                      setScannerVisible(!scannerVisible);
                    }}
                    variant={scannerVisible ? "secondary" : "primary"}
                    size="sm"
                    className="h-8"
                  />

                  {/* Barcode input */}
                  <input
                    type="text"
                    placeholder={t("dispatcher.orEnterBarcodeManually")}
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    className="flex-1 h-8 border border-ui-border rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                  />

                  {/* Select button when package exists */}
                  {packageExists && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, x: 10 }}
                      animate={{ opacity: 1, scale: 1, x: 0 }}
                      transition={{
                        duration: 0.2,
                        ease: [0.25, 0.46, 0.45, 0.94], // easeOutQuart
                      }}
                    >
                      <BoltButton
                        label={t("dispatcher.selectCheck")}
                        onClick={() => handlePickupByBarcode(barcodeInput)}
                        variant="success"
                        size="sm"
                        className="h-8"
                      />
                    </motion.div>
                  )}
                </div>

                {/* Embedded Scanner Section */}
                {scannerVisible && (
                  <div className="mt-3">
                    <div
                      className="relative bg-black rounded-lg overflow-hidden"
                      style={{ height: "200px" }}
                    >
                      {/* Scanner Component */}
                      <BarcodeScanner
                        setData={handleScanSuccess}
                        handleClose={() => setScannerVisible(false)}
                        closeOnScan={false}
                      />

                      {/* Scan toast */}
                      <AnimatePresence>
                        {scanAlert.show && (
                          <motion.div
                            initial={{ opacity: 0, y: -20, scale: 0.95 }}
                            animate={{ opacity: 1, y: 5, scale: 1 }}
                            exit={{ opacity: 0, y: -20, scale: 0.95 }}
                            transition={{
                              duration: 0.25,
                              ease: [0.25, 0.46, 0.45, 0.94],
                            }}
                            className={`absolute top-2 left-2 z-20 px-2 py-1 rounded text-white text-xs ${
                              scanAlert.type === "error"
                                ? "bg-state-danger"
                                : "bg-state-success"
                            }`}
                          >
                            <div className="flex items-center gap-1">
                              {scanAlert.type === "error" ? (
                                <span>⚠</span>
                              ) : (
                                <BiSolidCheckCircle size={12} />
                              )}
                              <span className="font-medium">
                                {scanAlert.type === "error"
                                  ? t("dispatcher.invalid")
                                  : t("dispatcher.scanned")}
                              </span>
                              <span>{scanAlert.barcode}</span>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Scanner overlay UI */}
                      <div className="absolute top-2 right-2 z-10">
                        <button
                          onClick={() => setScannerVisible(false)}
                          className="bg-black/50 text-white p-1.5 rounded-full hover:bg-black/70 transition-colors"
                        >
                          <AiOutlineClose size={14} />
                        </button>
                      </div>

                      {/* Scanner instructions */}
                      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10">
                        <div className="bg-black/70 text-white px-2 py-1 rounded text-xs text-center">
                          {t("dispatcher.pointCameraAtBarcode")}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bulk Actions Panel - Always show if there are packages */}
              {shipmentData?.shipment_details?.length > 0 && (
                <div className="mt-3">
                  <div className="mb-2">
                    <h3 className="text-xs font-semibold text-ui-text">
                      {t("dispatcher.bulkActions")}{" "}
                      {selectedIds.length > 0 &&
                        t("dispatcher.selectedCount", { count: selectedIds.length })}
                    </h3>
                  </div>

                  {/* Message when no packages selected */}
                  {selectedIds.length === 0 && (
                    <div className="p-3 bg-ui-surface-sunken rounded-lg border-2 border-dashed border-ui-border text-center">
                      <div className="text-ui-text-subtle mb-1">📦</div>
                      <p className="text-xs text-ui-text-muted font-medium mb-1">
                        {t("dispatcher.selectPackagesToPerformBulk")}
                      </p>
                      <p className="text-[10px] text-ui-text-subtle">
                        {t("dispatcher.bulkHelp")}
                      </p>
                    </div>
                  )}

                  {selectedIds.length > 0 && (
                    <div className="space-y-1">
                      {/* Tabs at the top */}
                      <div
                        className={`grid gap-1 ${
                          shouldShowSignature() ? "grid-cols-4" : "grid-cols-3"
                        }`}
                      >
                        {/* Status Button - Always active, cannot be toggled */}
                        <BoltButton
                          icon={FaCheck}
                          iconSize={8}
                          label={t("dispatcher.status")}
                          onClick={() => toggleBulkAction("status")}
                          variant="primary"
                          size="sm"
                          className="h-8 w-full opacity-100 cursor-default"
                        />

                        {/* Image Button */}
                        <BoltButton
                          icon={FaImage}
                          iconSize={8}
                          label={t("dispatcher.image")}
                          onClick={() => toggleBulkAction("image")}
                          variant={
                            bulkActiveActions.includes("image")
                              ? "primary"
                              : "outline"
                          }
                          size="sm"
                          className="h-8 w-full"
                        />

                        {/* Note Button */}
                        <BoltButton
                          icon={FaStickyNote}
                          iconSize={8}
                          label={t("dispatcher.note")}
                          onClick={() => toggleBulkAction("note")}
                          variant={
                            bulkActiveActions.includes("note")
                              ? "primary"
                              : "outline"
                          }
                          size="sm"
                          className="h-8 w-full"
                        />

                        {/* Signature Button - only show when delivered status is selected and signature is enabled */}
                        {shouldShowSignature() && (
                          <BoltButton
                            icon={FaPen}
                            iconSize={8}
                            label={t("dispatcher.sign")}
                            onClick={() => toggleBulkAction("signature")}
                            variant={
                              bulkActiveActions.includes("signature")
                                ? "primary"
                                : "outline"
                            }
                            size="sm"
                            className="h-8 w-full"
                          />
                        )}
                      </div>

                      {/* Content area - simplified styling */}
                      {bulkActiveActions.length > 0 && (
                        <div className="space-y-2">
                          {/* Status Tab */}
                          {bulkActiveActions.includes("status") && (
                            <div className="p-2 bg-ui-surface-sunken rounded-lg border">
                              <h4 className="text-xs font-semibold mb-1">
                                {t("dispatcher.updateStatusFor", { count: selectedIds.length })}
                              </h4>
                              {validationErrors.status && (
                                <p className="text-state-danger text-xs mb-1">
                                  {validationErrors.status}
                                </p>
                              )}
                              <select
                                value={bulkStatus}
                                onChange={(e) =>
                                  handleBulkStatusChange(e.target.value)
                                }
                                className={`w-full h-8 text-sm border rounded-lg px-3 pr-8 focus:outline-none focus:ring-2 focus:ring-brand ${
                                  validationErrors.status
                                    ? "border-red-300"
                                    : "border-ui-border"
                                }`}
                              >
                                <option value="">{t("dispatcher.selectStatusOption")}</option>
                                {trackingStatuses.map((s) => (
                                  <option key={s} value={s}>
                                    {t(`dispatcher.statuses.${s}`, { defaultValue: s })}
                                  </option>
                                ))}
                              </select>
                              <div className="mt-1">
                                <p className="text-xs text-ui-text-muted">
                                  {t("dispatcher.appliedTo", { count: selectedIds.length })}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Image Tab */}
                          {bulkActiveActions.includes("image") && (
                            <div className="p-2 bg-ui-surface-sunken rounded-lg border">
                              <h4 className="text-xs font-semibold mb-1">
                                {t("dispatcher.addImagesTo", { count: selectedIds.length })}
                              </h4>
                              <div className="mb-3">
                                <label className="block text-xs text-ui-text-muted mb-2">
                                  {t("dispatcher.uploadImagesHelp")}
                                </label>
                                <div
                                  className="relative"
                                  onDragOver={handleDragOver}
                                  onDragLeave={handleDragLeave}
                                  onDrop={handleDrop}
                                >
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    capture="environment"
                                    onChange={handleBulkImageChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                    id="bulk-image-upload"
                                  />
                                  <label
                                    htmlFor="bulk-image-upload"
                                    className={`flex flex-col items-center justify-center w-full h-20 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 ${
                                      isDragOver
                                        ? "border-brand bg-brand-light scale-[1.02]"
                                        : "border-brand bg-gradient-to-r from-brand-light to-brand-light hover:from-brand-light hover:to-brand-light hover:border-brand"
                                    }`}
                                  >
                                    <FaImage
                                      size={16}
                                      className={`mb-1 ${
                                        isDragOver
                                          ? "text-brand"
                                          : "text-brand"
                                      }`}
                                    />
                                    <span
                                      className={`text-xs font-medium ${
                                        isDragOver
                                          ? "text-brand-hover"
                                          : "text-brand-hover"
                                      }`}
                                    >
                                      {isDragOver
                                        ? t("dispatcher.dropImagesHere")
                                        : bulkImages.length > 0
                                        ? t("dispatcher.imagesSelected", {
                                            count: bulkImages.length,
                                          })
                                        : t("dispatcher.clickDragOrPasteImages")}
                                    </span>
                                    {!isDragOver && (
                                      <span className="text-[10px] text-ui-text-muted mt-0.5">
                                        {t("dispatcher.imageTypesHelp")}
                                      </span>
                                    )}
                                  </label>
                                </div>
                              </div>
                              {bulkImages.length > 0 && (
                                <div className="mb-3">
                                  <div className="flex flex-wrap gap-2">
                                    {bulkImages
                                      .slice(0, 3)
                                      .map((file, index) => (
                                        <div key={index} className="relative">
                                          <img
                                            src={URL.createObjectURL(file)}
                                            alt={t("dispatcher.previewAlt", { n: index + 1 })}
                                            className="w-16 h-16 rounded-lg object-cover border-2 border-ui-border"
                                          />
                                          {index === 2 &&
                                            bulkImages.length > 3 && (
                                              <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
                                                <span className="text-white text-xs font-semibold">
                                                  +{bulkImages.length - 3}
                                                </span>
                                              </div>
                                            )}
                                        </div>
                                      ))}
                                    <button
                                      onClick={handleBulkRetake}
                                      className="w-16 h-16 bg-state-danger-bg border-2 border-state-danger-border rounded-lg flex items-center justify-center hover:bg-state-danger-bg transition-colors"
                                    >
                                      <AiOutlineClose
                                        size={12}
                                        className="text-state-danger"
                                      />
                                    </button>
                                  </div>
                                </div>
                              )}
                              <div className="mt-1">
                                <p className="text-xs text-ui-text-muted">
                                  {t("dispatcher.appliedTo", { count: selectedIds.length })}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Note Tab */}
                          {bulkActiveActions.includes("note") && (
                            <div className="p-2 bg-ui-surface-sunken rounded-lg border">
                              <h4 className="text-xs font-semibold mb-1">
                                {t("dispatcher.addNoteTo", { count: selectedIds.length })}
                              </h4>
                              <div className="mb-3">
                                <label className="block text-xs text-ui-text-muted mb-1">
                                  {t("dispatcher.writeYourNote")}
                                </label>
                                <textarea
                                  value={bulkNote}
                                  onChange={(e) =>
                                    handleBulkNoteChange(e.target.value)
                                  }
                                  placeholder={t("dispatcher.enterNoteDetails")}
                                  className="w-full h-20 text-sm border border-ui-border rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-brand"
                                />
                              </div>
                              <div className="mt-1">
                                <p className="text-xs text-ui-text-muted">
                                  {t("dispatcher.appliedTo", { count: selectedIds.length })}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Signature Tab */}
                          {bulkActiveActions.includes("signature") && (
                            <div className="p-2 bg-ui-surface-sunken rounded-lg border">
                              <h4 className="text-xs font-semibold mb-1">
                                {t("dispatcher.addSignatureTo", { count: selectedIds.length })}
                              </h4>
                              <div className="mb-3">
                                <label className="block text-xs text-ui-text-muted mb-2">
                                  {t("dispatcher.captureSignature")}
                                </label>
                                {!bulkSignature ? (
                                  <button
                                    onClick={() => setSignatureModalOpen(true)}
                                    className="w-full h-24 bg-brand-light border-2 border-dashed border-brand rounded-lg cursor-pointer transition-all duration-200 flex flex-col items-center justify-center"
                                  >
                                    <FaPen
                                      size={18}
                                      className="text-brand mb-2"
                                    />
                                    <span className="text-sm font-medium text-brand-hover">
                                      {t("dispatcher.clickToSign")}
                                    </span>
                                  </button>
                                ) : (
                                  <div className="relative">
                                    <img
                                      src={URL.createObjectURL(bulkSignature)}
                                      alt={t("dispatcher.signaturePreview")}
                                      className="w-full h-24 rounded-lg object-contain border-2 border-ui-border bg-ui-surface"
                                    />
                                    <button
                                      onClick={() => setBulkSignature(null)}
                                      className="absolute -top-2 -right-2 w-6 h-6 bg-state-danger text-white rounded-full flex items-center justify-center hover:bg-state-danger transition-colors"
                                    >
                                      <AiOutlineClose size={12} />
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div className="mt-1">
                                <p className="text-xs text-ui-text-muted">
                                  {t("dispatcher.appliedTo", { count: selectedIds.length })}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {shipmentData?.shipment_details?.length > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-ui-text">
                      {t("dispatcher.packagesCount", { count: shipmentData.shipment_details.length })}
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={toggleSelectAll}
                        className="px-2 py-1 text-xs text-brand hover:text-brand-hover bg-brand-light hover:bg-brand-light rounded-md font-medium transition-colors"
                      >
                        {t("dispatcher.selectAll")}
                      </button>
                      {selectedIds.length > 0 && (
                        <button
                          onClick={clearSelection}
                          className="px-2 py-1 text-xs text-state-danger hover:text-state-danger bg-state-danger-bg hover:bg-state-danger-bg rounded-md font-medium transition-colors"
                        >
                          {t("dispatcher.clearCount", { count: selectedIds.length })}
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    {shipmentData.shipment_details.map((pkg) => {
                      const eligible = isEligibleForActions(pkg);
                      const checked = selectedIds.includes(pkg.barcode);
                      return (
                        <div key={pkg.id} className="mb-2">
                          <div
                            className={`relative h-20 p-2 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                              checked
                                ? "border-brand bg-brand-light shadow-sm"
                                : "border-ui-border bg-ui-surface hover:border-ui-border"
                            }`}
                            onClick={() => handlePackageClick(pkg)}
                          >
                            {/* Custom Checkbox */}
                            <div
                              className="absolute top-2 left-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all duration-200 ${
                                  checked
                                    ? "border-brand bg-brand"
                                    : "border-ui-border bg-ui-surface hover:border-brand"
                                }`}
                                onClick={() =>
                                  toggleSelect(pkg.barcode, eligible)
                                }
                              >
                                {checked && (
                                  <svg
                                    className="w-3 h-3 text-white"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                )}
                              </div>
                            </div>

                            {/* Right Arrow - Absolutely positioned */}
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                              <FaChevronRight
                                size={12}
                                className="text-ui-text-subtle"
                              />
                            </div>

                            {/* Package Content - Three Row Layout */}
                            <div className="ml-9 mr-10 flex flex-col space-y-1">
                              {/* First Row: ID + Barcode */}
                              <div className="flex items-center justify-between min-w-0">
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <span className="text-sm font-semibold text-ui-text">
                                    📦 {pkg.parcel_id}
                                  </span>
                                  {isPickedUpOrLater(pkg) && (
                                    <BiSolidCheckCircle
                                      size={12}
                                      className="text-state-success-text flex-shrink-0"
                                    />
                                  )}
                                </div>
                                <span className="text-[9px] text-ui-text-muted font-mono bg-ui-surface-sunken px-1.5 py-0.5 rounded flex-shrink-0 max-w-[80px] truncate">
                                  {pkg.barcode}
                                </span>
                              </div>

                              {/* Second Row: Package Details */}
                              <div className="flex items-center min-w-0">
                                <span className="text-[10px] text-ui-text-muted truncate">
                                  {pkg.weight}
                                  {shipmentData?.unit === "Imperial"
                                    ? "lb"
                                    : "kg"}{" "}
                                  • {pkg.length}×{pkg.width}×{pkg.height}
                                  {shipmentData?.unit === "Imperial"
                                    ? "in"
                                    : "cm"}{" "}
                                  • {pkg.volume}
                                  {shipmentData?.unit === "Imperial"
                                    ? "ft³"
                                    : "m³"}
                                </span>
                              </div>

                              {/* Third Row: Status and Images */}
                              <div className="flex items-center justify-between min-w-0">
                                <div className="flex items-center gap-1 min-w-0 flex-1">
                                  {(() => {
                                    const isUpdated = updatedPackages.includes(
                                      pkg.barcode
                                    );
                                    const formPkg = formData.find(
                                      (formPkg) => formPkg.id === pkg.barcode
                                    );
                                    const hasStatusChange =
                                      formPkg?.status &&
                                      formPkg.status !== pkg.tracking_status;
                                    const hasDescriptionChange =
                                      formPkg?.description &&
                                      formPkg.description.trim() !== "";

                                    if (isUpdated && hasStatusChange) {
                                      // Show old status (strikethrough) + new status
                                      return (
                                        <div className="flex items-center gap-1 min-w-0 max-w-[165px]">
                                          <span
                                            className="text-[8px] text-ui-text-muted line-through block truncate max-w-[52px]"
                                            title={pkg.tracking_status}
                                          >
                                            {pkg.tracking_status}
                                          </span>
                                          <span className="text-[8px] text-ui-text-subtle flex-shrink-0">
                                            →
                                          </span>
                                          <span
                                            className={`inline-block px-1 py-0.5 rounded-full text-[8px] font-medium truncate max-w-[82px] ${
                                              isPickedUpOrLater(pkg)
                                                ? "bg-state-success-bg text-green-800"
                                                : "bg-brand-light text-blue-800"
                                            }`}
                                            title={formPkg.status}
                                          >
                                            {formPkg.status}
                                          </span>
                                        </div>
                                      );
                                    } else {
                                      // Show current status normally
                                      return (
                                        <div className="flex items-center gap-1 min-w-0 max-w-[165px]">
                                          <span
                                            className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-medium truncate max-w-[127px] ${
                                              isPickedUpOrLater(pkg)
                                                ? "bg-state-success-bg text-green-800"
                                                : "bg-ui-surface-sunken text-ui-text"
                                            }`}
                                            title={
                                              formPkg?.status ||
                                              pkg.tracking_status
                                            }
                                          >
                                            {formPkg?.status ||
                                              pkg.tracking_status}
                                          </span>
                                          {hasDescriptionChange &&
                                            !hasStatusChange && (
                                              <span className="text-[7px] text-orange-600 font-medium flex-shrink-0">
                                                {t("dispatcher.bulletNote")}
                                              </span>
                                            )}
                                        </div>
                                      );
                                    }
                                  })()}
                                </div>

                                {/* Package Images */}
                                {(() => {
                                  const images = getPackageImages(pkg);
                                  if (images.length === 0) return null;

                                  return (
                                    <div className="flex items-center gap-1">
                                      <div
                                        className="relative cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedImageModal({
                                            isOpen: true,
                                            images: images,
                                            currentIndex: 0,
                                          });
                                        }}
                                      >
                                        <img
                                          src={images[0].src}
                                          alt={t("dispatcher.packageAlt")}
                                          className="w-6 h-6 rounded object-cover border border-ui-border hover:border-brand transition-colors"
                                        />
                                        {images.length > 1 && (
                                          <span className="absolute -top-1 -right-1 bg-brand text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center font-medium">
                                            +{images.length - 1}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex w-full justify-end pt-2 mt-2 border-t border-ui-border space-x-2">
                <Button
                  text={t("common:actions.cancel")}
                  variant="light"
                  onClick={handleModalClose}
                />
                <Button
                  text={t("dispatcher.updatePackagesStatus")}
                  onClick={handleUpdatePackages}
                  loading={updating}
                  disabled={!updatedPackages?.length}
                  className={`w-44 transition-all duration-200 ${
                    !updatedPackages?.length
                      ? "opacity-50 scale-95 cursor-not-allowed"
                      : "opacity-100 scale-100"
                  }`}
                />
              </div>
              {/* Collapsible Shipment History */}
              <div className="mt-4 border-t border-ui-border pt-3">
                <button
                  onClick={() => setHistoryCollapsed(!historyCollapsed)}
                  className="flex items-center justify-between w-full py-2 hover:bg-ui-surface-sunken rounded transition-colors duration-200"
                >
                  <h3 className="text-xs font-semibold text-ui-text">
                    {t("dispatcher.shipmentHistory")}
                    {shipmentData?.shipment_histories?.length && (
                      <span className="ml-2 text-xs text-ui-text-muted font-normal">
                        {t("dispatcher.historyEntries", { count: shipmentData.shipment_histories.length })}
                      </span>
                    )}
                  </h3>
                  <motion.div
                    animate={{ rotate: historyCollapsed ? 0 : 180 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaChevronDown size={10} className="text-ui-text-subtle" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {!historyCollapsed && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{
                        duration: 0.3,
                        ease: [0.4, 0.0, 0.2, 1],
                      }}
                      className="overflow-hidden"
                    >
                      <TrackingHistory
                        data={shipmentData?.shipment_histories}
                        heading=""
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Signature Modal */}
      <AnimatePresence>
        {signatureModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              transition={{
                duration: 0.25,
                ease: [0.25, 0.46, 0.45, 0.94], // easeOutQuart
              }}
              className="bg-ui-surface rounded-lg w-full max-w-md mx-auto"
            >
              <div className="p-4 border-b border-ui-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">{t("dispatcher.addSignature")}</h3>
                  <button
                    onClick={() => setSignatureModalOpen(false)}
                    className="text-ui-text-subtle hover:text-ui-text-muted transition-colors"
                  >
                    <AiOutlineClose size={20} />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <div className="mb-4">
                  <p className="text-sm text-ui-text-muted mb-4">
                    {t("dispatcher.pleaseSignInTheArea")}
                  </p>
                  <div className="border-2 border-dashed border-ui-border rounded-lg bg-ui-surface-sunken">
                    <canvas
                      id="signature-canvas"
                      width={400}
                      height={200}
                      className="w-full h-48 cursor-crosshair rounded-lg"
                      style={{ touchAction: "none" }}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <BoltButton
                    label={t("dispatcher.clear")}
                    onClick={() => {
                      const canvas =
                        document.getElementById("signature-canvas");
                      const ctx = canvas.getContext("2d");
                      ctx.clearRect(0, 0, canvas.width, canvas.height);
                    }}
                    variant="light"
                    size="md"
                    className="flex-1 h-10"
                  />
                  <BoltButton
                    label={t("dispatcher.saveSignature")}
                    onClick={() => {
                      const canvas =
                        document.getElementById("signature-canvas");
                      canvas.toBlob((blob) => {
                        if (blob) {
                          const signatureFile = new File(
                            [blob],
                            "signature.png",
                            {
                              type: "image/png",
                            }
                          );
                          handleBulkSignatureChange(signatureFile);
                          setSignatureModalOpen(false);
                        }
                      });
                    }}
                    variant="primary"
                    size="md"
                    className="flex-1 h-10"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Package Images Modal */}
      <AnimatePresence>
        {selectedImageModal.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/90 z-[300] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{
                duration: 0.25,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              className="relative bg-ui-surface rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden"
            >
              {/* Header */}
              <div className="p-4 border-b border-ui-border flex items-center justify-between">
                <h3 className="text-lg font-semibold text-ui-text">
                  {t("dispatcher.imagesOf", {
                    current: selectedImageModal.currentIndex + 1,
                    total: selectedImageModal.images.length,
                  })}
                </h3>
                <button
                  onClick={() =>
                    setSelectedImageModal({
                      isOpen: false,
                      images: [],
                      currentIndex: 0,
                    })
                  }
                  className="text-ui-text-subtle hover:text-ui-text-muted transition-colors"
                >
                  <AiOutlineClose size={24} />
                </button>
              </div>

              {/* Image Display */}
              <div
                className="relative flex items-center justify-center bg-ui-surface-sunken"
                style={{ height: "60vh" }}
              >
                {selectedImageModal.images.length > 0 && (
                  <img
                    src={
                      selectedImageModal.images[selectedImageModal.currentIndex]
                        .src
                    }
                    alt={t("dispatcher.packageNumberAlt", { n: selectedImageModal.currentIndex + 1 })}
                    className="max-w-full max-h-full object-contain"
                  />
                )}

                {/* Navigation arrows */}
                {selectedImageModal.images.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setSelectedImageModal((prev) => ({
                          ...prev,
                          currentIndex:
                            prev.currentIndex > 0
                              ? prev.currentIndex - 1
                              : prev.images.length - 1,
                        }))
                      }
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() =>
                        setSelectedImageModal((prev) => ({
                          ...prev,
                          currentIndex:
                            prev.currentIndex < prev.images.length - 1
                              ? prev.currentIndex + 1
                              : 0,
                        }))
                      }
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {selectedImageModal.images.length > 1 && (
                <div className="p-4 border-t border-ui-border">
                  <div className="flex gap-2 overflow-x-auto">
                    {selectedImageModal.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() =>
                          setSelectedImageModal((prev) => ({
                            ...prev,
                            currentIndex: index,
                          }))
                        }
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                          index === selectedImageModal.currentIndex
                            ? "border-brand"
                            : "border-ui-border hover:border-ui-border"
                        }`}
                      >
                        <img
                          src={image.src}
                          alt={t("dispatcher.thumbnailAlt", { n: index + 1 })}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 text-center">
                    <span className="text-sm text-ui-text-muted">
                      {t("dispatcher.imageCounts", {
                        historical: selectedImageModal.images.filter(
                          (img) => img.type === "historical"
                        ).length,
                        fresh: selectedImageModal.images.filter(
                          (img) => img.type === "new"
                        ).length,
                      })}
                    </span>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ModalWrapper>
  );
};

export default ShipmentStatusModal;
